import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faRightLeft } from "@fortawesome/free-solid-svg-icons/faRightLeft";

import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { Entry } from "../../../model/entry/Entry";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import {
  DuplicateEntryFieldDescriptor,
  isValueCovered,
} from "../utils/duplicateEntryFields";
import { ActionButton, ClampedText } from "./DuplicateCardPrimitives";
import { TestIDs } from "../../../tests/types/testIds";

const Row = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.s};
  padding: ${Spacing.s} ${Spacing.m};
  border-radius: ${Rounded.lg};
  border: 1px solid ${({ theme }) => theme.darkSub};
  background-color: ${({ theme }) => theme.lightSub};
`;

const ValueLine = styled.div`
  display: grid;
  grid-template-columns: 5.5rem minmax(0, 1fr);
  align-items: baseline;
  gap: ${Spacing.s};
`;

type Props = {
  descriptor: DuplicateEntryFieldDescriptor;
  entry: Entry;
  finalEntry: Entry;
  onUpdate: ATVoidFunction<{
    field: string;
    value: string;
    mode: "append" | "set";
  }>;
};

export const EntryDuplicateFieldCompareRow: React.FC<Props> = ({
  descriptor,
  entry,
  finalEntry,
  onUpdate,
}) => {
  const { t } = useTranslation();

  const keptValue = descriptor.getValue(finalEntry);
  const otherValue = descriptor.getValue(entry);

  if (isValueCovered(keptValue, otherValue, descriptor)) {
    return null;
  }

  const keptDisplay = descriptor.formatValue(finalEntry);
  const otherDisplay = descriptor.formatValue(entry);
  const apply = (mode: "append" | "set") =>
    onUpdate({ field: descriptor.key, value: otherValue, mode });
  const actionTestId = (action: "use" | "replace" | "append") =>
    TestIDs.entries.duplicates.resolve.fieldAction(
      entry._id,
      descriptor.key,
      action,
    );

  return (
    <Row>
      <Text fontSize="s" fontWeight="bold">
        {descriptor.label}
      </Text>

      <div>
        <ValueLine>
          <Text fontSize="xs" color="darkerSub">
            {t("entries.duplicates.card.field.kept")}
          </Text>
          {keptDisplay ? (
            <ClampedText fontSize="s">
              <bdi>{keptDisplay}</bdi>
            </ClampedText>
          ) : (
            <Text fontSize="s" color="darkerSub">
              -
            </Text>
          )}
        </ValueLine>

        <ValueLine>
          <Text fontSize="xs" color="darkerSub">
            {t("entries.duplicates.card.field.this")}
          </Text>
          <ClampedText fontSize="s">
            <bdi>{otherDisplay}</bdi>
          </ClampedText>
        </ValueLine>
      </div>

      <Flex gap="s" flexWrap="wrap">
        {keptValue ? (
          <>
            <ActionButton
              fontSize="xs"
              icon={faRightLeft}
              dataTestId={actionTestId("replace")}
              info={t("entries.duplicates.card.field.actions.replace.tooltip", {
                value: otherDisplay,
              })}
              onClick={() => apply("set")}
            >
              {t("entries.duplicates.card.field.actions.replace")}
            </ActionButton>

            {descriptor.canAppend(entry) ? (
              <ActionButton
                fontSize="xs"
                icon={faPlus}
                dataTestId={actionTestId("append")}
                info={t(
                  "entries.duplicates.card.field.actions.append.tooltip",
                  {
                    result: `${keptValue}, ${otherValue}`,
                  },
                )}
                onClick={() => apply("append")}
              >
                {t("entries.duplicates.card.field.actions.append")}
              </ActionButton>
            ) : null}
          </>
        ) : (
          <ActionButton
            fontSize="xs"
            icon={faCheck}
            dataTestId={actionTestId("use")}
            info={t("entries.duplicates.card.field.actions.use.tooltip")}
            onClick={() => apply("set")}
          >
            {t("entries.duplicates.card.field.actions.use")}
          </ActionButton>
        )}
      </Flex>
    </Row>
  );
};
